import * as React from 'react';
import { enqueueSnackbar } from 'notistack';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Drawer,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ModalCloseButton } from '@coverright/ui/shared';
import {
  ArrowSquareOut,
  Bell,
  CalendarBlank,
  CaretDown,
  CaretUp,
  Copy,
  IdentificationCard,
  MapPin,
  Pencil,
  Phone,
  TextAlignLeft,
  Users,
  VideoCamera,
} from '@phosphor-icons/react';
import moment from 'moment-timezone';
import {
  CalendarEventAttendeeOutput,
  CalendarEventOutput,
  CalendarEventResponse,
  useRespondToCalendarEventMutation,
} from '../calendar-types';
import { getEventColor } from './calendar-colors';
import { EventFormModal } from './event-form-modal';
import { isCalendarAuthError } from './calendar-utils';

interface EventDetailDrawerProps {
  event: CalendarEventOutput | null;
  onClose: () => void;
  agentEmail?: string | null;
  onMutationComplete?: (eventId: string | undefined) => void;
}

const RSVP_LABELS: Record<string, string> = {
  accepted: 'Accepted',
  declined: 'Declined',
  tentative: 'Tentative',
  needsAction: 'Awaiting',
};


const RSVP_OPTIONS = [
  { label: 'Yes', value: CalendarEventResponse.Accepted, rsvpKey: 'accepted' },
  { label: 'No', value: CalendarEventResponse.Declined, rsvpKey: 'declined' },
  { label: 'Maybe', value: CalendarEventResponse.Tentative, rsvpKey: 'tentative' },
] as const;

const formatDateTime = (start?: string | null, end?: string | null, tz?: string | null) => {
  if (!start) return { line1: '', line2: '' };
  const s = tz ? moment.tz(start, tz) : moment(start);
  const e = end ? (tz ? moment.tz(end, tz) : moment(end)) : null;
  const line1 = s.format('dddd, MMMM D');
  const timeRange = e ? `${s.format('h:mm a')} – ${e.format('h:mm a')}` : s.format('h:mm a');
  const line2 = tz ? `${timeRange} (${tz})` : timeRange;
  return { line1, line2 };
};

const InfoRow = ({
  icon,
  children,
  sx,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  sx?: Record<string, unknown>;
}) => (
  <Stack direction="row" gap={2} alignItems="flex-start" sx={sx}>
    <Box sx={{ width: 20, display: 'flex', justifyContent: 'center', flexShrink: 0, mt: '2px', color: 'text.secondary' }}>
      {icon}
    </Box>
    <Box flex={1} minWidth={0}>
      {children}
    </Box>
  </Stack>
);

const RSVP_CHIP_COLORS: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  accepted: 'success',
  declined: 'error',
  tentative: 'warning',
  needsAction: 'default',
};

const AttendeeRow = ({ attendee }: { attendee: CalendarEventAttendeeOutput }) => {
  const rsvp = attendee.responseStatus || 'needsAction';
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" py={0.5}>
      <Box minWidth={0} flex={1} mr={1}>
        <Typography variant="body2" noWrap fontWeight={attendee.self ? 600 : 400}>
          {attendee.displayName || attendee.email}{attendee.self ? ' (you)' : ''}
        </Typography>
        {attendee.displayName && (
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {attendee.email}
          </Typography>
        )}
      </Box>
      <Chip
        label={RSVP_LABELS[rsvp] || rsvp}
        size="small"
        color={RSVP_CHIP_COLORS[rsvp] || 'default'}
        variant="outlined"
        sx={{ fontSize: 10, height: 20, flexShrink: 0 }}
      />
    </Stack>
  );
};

export const EventDetailDrawer = ({ event, onClose, agentEmail, onMutationComplete }: EventDetailDrawerProps) => {
  const [editOpen, setEditOpen] = React.useState(false);
  const [guestsExpanded, setGuestsExpanded] = React.useState(false);
  const [respond, { loading: rsvpLoading }] = useRespondToCalendarEventMutation();

  React.useEffect(() => {
    if (!event) { setEditOpen(false); setGuestsExpanded(false); }
  }, [event?.id]);

  const myAttendee = event?.attendees?.find((a) => a.self);
  const myRsvp = myAttendee?.responseStatus;

  const handleRsvp = async (response: CalendarEventResponse) => {
    if (!event?.id) return;
    try {
      await respond({ variables: { eventId: event.id, response, agentEmail }, refetchQueries: ['calendarEvents'] });
      onMutationComplete?.(event.id);
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : 'Failed to update RSVP';
      const msg = isCalendarAuthError(raw)
        ? "Agent's Google account is not connected to the workspace."
        : raw;
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const isCancelled = event?.status === 'cancelled';
  const isTentative = event?.status === 'tentative';

  const attendees = event?.attendees ?? [];
  const accepted = attendees.filter((a) => a.responseStatus === 'accepted').length;
  const tentative = attendees.filter((a) => a.responseStatus === 'tentative').length;
  const awaiting = attendees.filter((a) => !a.responseStatus || a.responseStatus === 'needsAction').length;
  const declined = attendees.filter((a) => a.responseStatus === 'declined').length;

  const guestSummaryParts: string[] = [];
  if (accepted) guestSummaryParts.push(`${accepted} yes`);
  if (tentative) guestSummaryParts.push(`${tentative} maybe`);
  if (declined) guestSummaryParts.push(`${declined} no`);
  if (awaiting) guestSummaryParts.push(`${awaiting} awaiting`);

  const { line1, line2 } = formatDateTime(event?.startDateTime, event?.endDateTime, event?.timeZone);
  const eventColor = event ? getEventColor(event.colorId, event.status) : null;

  return (
    <>
      <Drawer
        anchor="right"
        open={!!event}
        onClose={onClose}
        PaperProps={{ sx: { width: 400, p: 0, display: 'flex', flexDirection: 'column' } }}
        ModalProps={{ keepMounted: false }}
      >
        {event && (
          <Stack height="100%" overflow="hidden">
            {/* Top toolbar */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              px={1}
              py={1}
              gap={0.5}
              sx={{ flexShrink: 0 }}
            >
              <Tooltip title="Edit">
                <IconButton size="small" onClick={() => setEditOpen(true)}>
                  <Pencil size={18} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open in Google Calendar">
                <IconButton
                  size="small"
                  component="a"
                  href={event.htmlLink ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  disabled={!event.htmlLink}
                >
                  <ArrowSquareOut size={18} />
                </IconButton>
              </Tooltip>
              <ModalCloseButton onClick={onClose} size="small" />
            </Stack>

            {/* Scrollable content */}
            <Box px={3} pb={2} overflow="auto" flex={1}>
              {/* Title row — same icon column as InfoRow */}
              <Stack direction="row" gap={2} alignItems="flex-start" mb={1}>
                <Box sx={{ width: 20, display: 'flex', justifyContent: 'center', flexShrink: 0, mt: '5px' }}>
                  <Box sx={{ width: 14, height: 14, borderRadius: 0.5, backgroundColor: eventColor?.border }} />
                </Box>
                <Box flex={1} minWidth={0}>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{
                      textDecoration: isCancelled ? 'line-through' : 'none',
                      color: isCancelled ? 'text.disabled' : 'text.primary',
                    }}
                  >
                    {event.summary || '(No title)'}
                  </Typography>
                  {(isCancelled || isTentative) && (
                    <Stack direction="row" gap={0.75} mt={0.5}>
                      {isCancelled && (
                        <Chip label="Cancelled" size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                      )}
                      {isTentative && !isCancelled && (
                        <Chip label="Tentative" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                      )}
                    </Stack>
                  )}
                </Box>
              </Stack>

              {/* Date + time */}
              <InfoRow icon={<CalendarBlank size={18} weight="regular" />} sx={{ mb: 2 }}>
                <Typography variant="body2">{line1}</Typography>
                <Typography variant="body2" color="text.secondary">{line2}</Typography>
              </InfoRow>

              {/* Organizer */}
              {event.organizerName && (
                <InfoRow icon={<IdentificationCard size={18} />} sx={{ mb: 2 }}>
                  <Typography variant="body2">{event.organizerName}</Typography>
                </InfoRow>
              )}

              {/* Location */}
              {event.location && (
                <InfoRow icon={<MapPin size={18} />} sx={{ mb: 2 }}>
                  <Typography variant="body2">{event.location}</Typography>
                </InfoRow>
              )}

              {/* Description */}
              {event.description && (
                <InfoRow icon={<TextAlignLeft size={18} />} sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {event.description}
                  </Typography>
                </InfoRow>
              )}

              {/* Meet + Phone */}
              {(event.meetLink || event.dialInPhone) && (
                <InfoRow icon={<VideoCamera size={18} />} sx={{ mb: 2 }}>
                  {event.meetLink && (
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Link
                        href={event.meetLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        variant="body2"
                        fontWeight={500}
                        sx={{ color: 'primary.main' }}
                      >
                        Join with Google Meet
                      </Link>
                      <Tooltip title="Copy link">
                        <IconButton
                          size="small"
                          onClick={() => navigator.clipboard.writeText(event.meetLink!)}
                        >
                          <Copy size={14} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                  {event.dialInPhone && (
                    <Stack direction="row" alignItems="flex-start" gap={1} mt={event.meetLink ? 0.75 : 0}>
                      <Phone size={15} style={{ marginTop: 2, flexShrink: 0 }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {event.dialInPhone}
                          {event.dialInPin && ` PIN: ${event.dialInPin}#`}
                        </Typography>
                        {event.morePhoneNumbersUrl && (
                          <Link
                            href={event.morePhoneNumbersUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="caption"
                            underline="hover"
                          >
                            More phone numbers
                          </Link>
                        )}
                      </Box>
                    </Stack>
                  )}
                </InfoRow>
              )}

              {/* Guests */}
              {attendees.length > 0 && (
                <InfoRow icon={<Users size={18} />} sx={{ mb: 2 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setGuestsExpanded((v) => !v)}
                  >
                    <Box>
                      <Typography variant="body2">
                        {attendees.length} guest{attendees.length !== 1 ? 's' : ''}
                      </Typography>
                      {guestSummaryParts.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {guestSummaryParts.join(' · ')}
                        </Typography>
                      )}
                    </Box>
                    <IconButton size="small">
                      {guestsExpanded ? <CaretUp size={14} /> : <CaretDown size={14} />}
                    </IconButton>
                  </Stack>
                  <Collapse in={guestsExpanded}>
                    <Box mt={0.5}>
                      {attendees.map((a) => (
                        <AttendeeRow key={a.email} attendee={a} />
                      ))}
                    </Box>
                  </Collapse>
                </InfoRow>
              )}

              {/* Reminder */}
              {event.reminderMinutes != null && (
                <InfoRow icon={<Bell size={18} />} sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    {event.reminderMinutes < 60
                      ? `${event.reminderMinutes} minutes before`
                      : event.reminderMinutes === 60
                      ? '1 hour before'
                      : event.reminderMinutes === 1440
                      ? '1 day before'
                      : `${event.reminderMinutes / 60} hours before`}
                  </Typography>
                </InfoRow>
              )}
            </Box>

            {/* RSVP footer — only shown when the agent is an attendee */}
            {myAttendee && (
              <Box
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  px: 3,
                  py: 1.5,
                  flexShrink: 0,
                  backgroundColor: 'background.paper',
                }}
              >
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 56 }}>
                    Going?
                  </Typography>
                  <Stack direction="row" gap={1}>
                    {RSVP_OPTIONS.map(({ label, value, rsvpKey }) => {
                      const isActive = myRsvp === rsvpKey;
                      return (
                        <Button
                          key={label}
                          size="small"
                          variant={isActive ? 'contained' : 'outlined'}
                          disableElevation
                          disabled={rsvpLoading}
                          onClick={() => handleRsvp(value)}
                          sx={{ minWidth: 64, fontWeight: isActive ? 600 : 400 }}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </Stack>
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </Drawer>

      <EventFormModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onDeleteComplete={() => { setEditOpen(false); onClose(); }}
        agentEmail={agentEmail}
        event={event}
        onMutationComplete={onMutationComplete}
      />
    </>
  );
};
